/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";

// Helper function to check if the art pack has been selected

// Components
import Table from "./Childs/Table";
import AdvanceSelector from "../../Common/Helpers/AdvanceSelector/AdvanceSelector";

// Common Components
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";

// Image for adding style color
import add_stylecolor from "../../../icons/add_stylecolor.svg";
import FormCard from "../../Common/Childs/Structure/FormCard";
import Delete from "./Delete";
import Form from "./Childs/Form";
import { useGlobal } from "../../../context/GlobalContextProvider";

// Initial state for the form data
const initialState = {
  id: 0,
  color_hex_code: "",
  transparency: "",
  stroke: "",
};

// Main component for selecting layer choice style color
const IndexLayerChoiceStyleColor = () => {
  const { artPack, setSpinner } = useGlobal();

  // State to hold various data
  const [data, setData] = useState([]); // Table data
  const [patternList, setPatternList] = useState([]); // List of patterns
  const [patternId, setPatternId] = useState(0); // Selected pattern ID
  const [layerList, setLayerList] = useState([]); // List of layers
  const [layerId, setLayerId] = useState(0); // Selected layer ID
  const [layerChoiceList, setLayerChoiceList] = useState([]); // List of layer choices
  const [layerChoiceId, setLayerChoiceId] = useState(0); // Selected layer choice ID
  const [layerChoiceClassList, setLayerChoiceClassList] = useState([]); // List of layer choice classes
  const [layerChoiceClassId, setLayerChoiceClassId] = useState(0); // Selected layer choice class ID
  const [showForm, setShowForm] = useState(false); // Flag to show/hide form
  const [formData, setFormData] = useState(initialState); // Data for the form

  // Fetch all patterns when the art pack changes
  useEffect(() => {
    const getDataPattern = async () => {
      const res = await fetch(`/api/artpack/${artPack?.art_pack_id}/pattern`);

      if (res.status === 200) {
        const json = await res.json();
        const data = json.map((el) => ({
          id: el.pattern_id,
          name: el.pattern_name,
        }));

        setPatternList(data);
        setPatternId(data ? data[0]?.id : 0); // Set default pattern ID
      }
    };
    if (artPack?.art_pack_id) getDataPattern();
  }, [artPack]);

  // Fetch layers filtered by selected pattern ID
  useEffect(() => {
    setLayerList([]); // Clear previous layer list
    const getLayerByPattern = async () => {
      const res = await fetch(`/api/pattern/${patternId}/layer`);

      if (res.status === 200) {
        const json = await res.json();
        const data = json.map((el) => ({
          id: el.layer_id,
          name: el.layer_name,
        }));
        setLayerList(data);
        setLayerId(data ? data[0]?.id : 0); // Set default layer ID
      }
    };
    if (patternId) getLayerByPattern();
  }, [patternId]);

  // Fetch layer choices filtered by selected layer ID
  useEffect(() => {
    setLayerChoiceList([]); // Clear previous layer choice list
    const getLayerChoiceByLayer = async () => {
      const res = await fetch(`/api/layer/${layerId}/layerchoice`);

      if (res.status === 200) {
        const json = await res.json();
        const data = json.map((el) => ({
          id: el.layer_choice_id,
          name: el.layer_url,
        }));
        setLayerChoiceList(data);
        setLayerChoiceId(data ? data[0]?.id : 0); // Set default layer choice ID
      }
    };
    if (layerId) getLayerChoiceByLayer();
  }, [layerId]);

  // Fetch layer choice classes filtered by selected layer choice ID
  useEffect(() => {
    setLayerChoiceClassList([]); // Clear previous layer choice class list
    const getDataLayerChoiceClass = async () => {
      const res = await fetch(`/api/layerchoice/${layerChoiceId}/layerchoiceclass`);

      if (res.status === 200) {
        const json = await res.json();
        const data = json.map((el) => ({
          id: el.layer_class_id,
          name: el.layer_class_name,
        }));

        setLayerChoiceClassList(data);
        setLayerChoiceClassId(data ? data[0]?.id : 0); // Set default layer choice class ID
      }
    };
    if (layerChoiceId) getDataLayerChoiceClass();
  }, [layerChoiceId]);

  // Load style color data for the selected layer choice class
  const loadTableData = async () => {
    setSpinner(true); // Show spinner while loading data
    const res = await fetch(`/api/layerclass/${layerChoiceClassId}/layerchoicestylecolor`);

    if (res.status === 200) {
      const json = await res.json();
      const data = json.map((el) => ({
        layer_style_color_id: el.layer_style_color_id,
        color_hex_code: el.color_hex_code,
        layer_class_id: el.layer_class_id,
        transparency: el.transparency,
        stroke: el.stroke,
      }));
      setData(data); // Set the fetched data to state
    }
    setSpinner(false); // Hide spinner after loading data
  };

  // Load table data whenever layer choice class ID changes
  useEffect(() => {
    if (layerChoiceClassId) loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerChoiceClassId]);

  // Create a new style color entry
  const createItem = async (data) => {
    await fetch(`/api/layerclass/${layerChoiceClassId}/layerchoicestylecolor`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Reload table data after creation
      onDiscard(); // Reset form state
    });
  };

  // Update an existing style color entry
  const updateItem = async (data) => {
    const { id } = formData; // Get ID from form data
    const url = `/api/layerclass/${layerChoiceClassId}/layerchoicestylecolor/${id}`;

    await fetch(url, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Reload table data after update
      onDiscard(); // Reset form state
    });
  };

  // Delete an existing style color entry
  const deleteItem = async () => {
    const { id } = formData; // Get ID from form data
    const url = `/api/layerclass/${layerChoiceClassId}/layerchoicestylecolor/${id}`;

    await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Reload table data after deletion
      onDiscard(); // Reset form state
    });
  };

  // Open the edit form with pre-filled data
  const onEdit = (data) => {
    setShowForm(true);
    setFormData(data);
  };

  // Open the delete confirmation form
  const onDelete = (data) => {
    setShowForm(false);
    setFormData(data);
  };

  // Reset the form state
  const onDiscard = () => {
    setShowForm(false);
    setFormData(initialState);
  };

  // Render form for editing or creating a style color
  if (showForm) {
    return (
      <>
        <Title title="Pattern" />
        <Description />
        <FormCard>
          <Form
            handleSubmit={formData?.color_hex_code ? updateItem : createItem} // Determine submit action based on form data
            formData={formData}
            formDataPattern={patternList}
            onDiscard={onDiscard}
          />
        </FormCard>
      </>
    );
  }

  // Render delete confirmation if an item is being deleted
  if (!showForm && formData?.id && formData?.delete) {
    return (
      <Delete
        onDiscard={onDiscard}
        handleSubmit={deleteItem}
        formData={formData}
      />
    );
  }

  // Render main component UI
  return (
    <>
      <Title title="Choice Style Color" />
      <Description />

      <div className="row m-button-add">
        <div className="mb-4">
          {/* Pattern Selector */}
          <AdvanceSelector
            data={patternList}
            module="pattern"
            selected={patternId}
            onChange={(id) => setPatternId(id)} // Update selected pattern ID
          />
        </div>
        <div className="mb-4">
          {/* Layer Selector */}
          <AdvanceSelector
            data={layerList}
            module="layer"
            selected={layerId}
            onChange={(id) => setLayerId(id)} // Update selected layer ID
          />
        </div>
        <div className="mb-4">
          {/* Layer Choice Selector */}
          <AdvanceSelector
            data={layerChoiceList}
            module="layerchoice"
            selected={layerChoiceId}
            onChange={(id) => setLayerChoiceId(id)} // Update selected layer choice ID
          />
        </div>
        <div className="mb-4">
          {/* Layer Choice Class Selector */}
          <AdvanceSelector
            data={layerChoiceClassList}
            module="layerchoiceclass"
            selected={layerChoiceClassId}
            onChange={(id) => setLayerChoiceClassId(id)} // Update selected layer choice class ID
          />
        </div>
        {/* Button to add a new style color */}
        <div className="col-1 button-add">
          <img
            src={add_stylecolor}
            alt="Add style color"
            onClick={() => setShowForm(true)} // Show form to add new style color
          />
        </div>
      </div>

      {/* Data Table */}
      <Table
        data={data}
        onEdit={onEdit} // Open edit form with selected data
        onDelete={onDelete} // Open delete confirmation with selected data
      />
    </>
  );
};

export default IndexLayerChoiceStyleColor;
